import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import {
  api,
  type DateScoringMode,
  type QuestionDisplayFormat,
  type QuestionResultsFormat,
  type ScoringMode,
} from '../api.js';
import {
  MAX_ANSWERS,
  MIN_ANSWERS,
  newAnswerDraft,
  type AnswerDraft,
  type QuestionBlockDraft,
} from '../lib/customQuestionDraft.js';
import { splitDateTime } from '../lib/intl.js';
import AnswerAppearanceEditor from './AnswerAppearanceEditor.js';
import DateField from './DateField.js';
import OptionGroup from './OptionGroup.js';
import RadioWithHelp, { DATE_SCORING_MODES, useDateScoringText } from './RadioWithHelp.js';
import styles from './QuestionTypeBlockEditor.module.css';

/**
 * One "Question type" block: the dropdown that picks what kind of question
 * this is, and — once a kind is picked — the editor for its own fields plus
 * the two fields every kind shares: an optional deadline and optional
 * lock-forever permission (PLAN.md Phase 10.3/10.4).
 *
 * NOT A QUIZ: for `multiple_choice`, the organizer picks a points RANGE for
 * "per_answer" (never a value per answer) and nothing at all for
 * "winner_loser"; for `football`, the organizer picks (or types) the two
 * teams, never a score. There is no correct-answer/real-result input here —
 * that's a separate, later step (`EditQuestionResultsScreen`), once the
 * real outcome is known.
 *
 * Collapsed by default once a kind is already chosen (PLAN.md Phase 10.17):
 * the Baby Shower template alone opens with 18 of these, and a screen that
 * shows every field of every one of them at once is not something anyone
 * can scan. A block with no kind chosen yet (`kind === ''` — a fresh "+ Add
 * question type" block, or one of the template's trailing blank ones) has
 * nothing to hide, so it starts open. Collapsing is purely local display
 * state, decided once per block instance; it never touches the draft data
 * `onChange` carries, so nothing about what gets submitted depends on
 * whether a block happens to be open when the organizer presses Create.
 *
 * Used both by `CreateCustomCompetitionScreen` (one or more of these, each
 * addable/removable) and `AddQuestionScreen` (exactly one) — kept as a
 * single component so the two never render the editor differently.
 */
export default function QuestionTypeBlockEditor({
  block,
  index,
  onChange,
  onRemove,
}: {
  block: QuestionBlockDraft;
  /** 1-based position among its siblings, shown in the collapsed header. Omitted where there's only ever one block (`AddQuestionScreen`). */
  index?: number;
  onChange: (next: QuestionBlockDraft) => void;
  /** Omit to hide the remove control — used when this is the only/last block. */
  onRemove?: () => void;
}) {
  const { t } = useTranslation();
  const [lockHelpOpen, setLockHelpOpen] = useState(false);
  const [open, setOpen] = useState(block.kind === '');

  const patchAnswer = (key: string, patch: Partial<AnswerDraft>) => {
    onChange({
      ...block,
      answers: block.answers.map((a) => (a.key === key ? { ...a, ...patch } : a)),
    });
  };

  const timingFields = (
    <>
      <div className={styles.subField}>
        <label className="label" htmlFor={`deadline-date-${block.key}`}>
          {t('customCompetition.deadlineLabel')}
        </label>
        <div className={styles.deadlineRow}>
          <DateField
            id={`deadline-date-${block.key}`}
            type="date"
            value={block.deadlineDate}
            onChange={(deadlineDate) => onChange({ ...block, deadlineDate })}
          />
          <DateField
            type="time"
            value={block.deadlineTime}
            ariaLabel={t('customCompetition.deadlineTimeLabel')}
            onChange={(deadlineTime) => onChange({ ...block, deadlineTime })}
          />
        </div>
        <p className="muted" style={{ fontSize: '0.75rem', marginTop: 'var(--space-1)' }}>
          {t('customCompetition.deadlineHint')}
        </p>
      </div>

      <div className={styles.subField}>
        <label className={styles.lockToggle}>
          <input
            type="checkbox"
            checked={block.allowLockForever}
            onChange={(e) => onChange({ ...block, allowLockForever: e.target.checked })}
          />
          {t('customCompetition.allowLockForeverLabel')}
          <button
            type="button"
            className={styles.helpButton}
            aria-label={t('customCompetition.allowLockForeverHelpLabel')}
            onClick={(e) => {
              e.preventDefault();
              setLockHelpOpen((open) => !open);
            }}
          >
            ?
          </button>
        </label>
        {lockHelpOpen ? (
          <p className={styles.helpText}>{t('customCompetition.allowLockForeverHelp')}</p>
        ) : null}
      </div>
    </>
  );

  return (
    <div className={`panel ${styles.block}`}>
      <div className={styles.blockHeader}>
        <button
          type="button"
          className={styles.blockHeaderToggle}
          aria-expanded={open}
          aria-label={
            open ? t('customCompetition.collapseQuestion') : t('customCompetition.expandQuestion')
          }
          onClick={() => setOpen((o) => !o)}
        >
          {index !== undefined ? (
            <span className={styles.blockNumber}>
              {t('customCompetition.questionPill', { number: index })}
            </span>
          ) : null}
          <span className={styles.blockSummary}>{blockSummary(block, t)}</span>
          <span className={styles.blockChevron} aria-hidden="true">
            {open ? '▲' : '▼'}
          </span>
        </button>
        {onRemove ? (
          <button
            type="button"
            className={styles.removeBlock}
            onClick={onRemove}
            aria-label={t('common.remove')}
          >
            ×
          </button>
        ) : null}
      </div>

      {open ? (
        <>
          <div>
            <label className="label" htmlFor={`kind-${block.key}`}>
              {t('customCompetition.questionTypeLabel')}
            </label>
            <select
              id={`kind-${block.key}`}
              className="select"
              value={block.kind}
              onChange={(e) =>
                onChange({ ...block, kind: e.target.value as QuestionBlockDraft['kind'] })
              }
            >
              <option value="">{t('customCompetition.questionTypePlaceholder')}</option>
              <option value="multiple_choice">{t('customCompetition.kindMultipleChoice')}</option>
              <option value="football">{t('customCompetition.kindFootball')}</option>
              <option value="date">{t('customCompetition.kindGuessTheDate')}</option>
            </select>
            <p className="muted" style={{ fontSize: '0.8rem', marginTop: 'var(--space-1)' }}>
              {t('customCompetition.questionTypeHint')}
            </p>
          </div>

          {block.kind === 'football' ? (
            <FootballQuestionEditor block={block} onChange={onChange}>
              <OptionGroup title={t('customCompetition.groupTiming')}>{timingFields}</OptionGroup>
            </FootballQuestionEditor>
          ) : null}

          {block.kind === 'date' ? (
            <DateQuestionEditor block={block} onChange={onChange}>
              <OptionGroup title={t('customCompetition.groupTiming')}>{timingFields}</OptionGroup>
            </DateQuestionEditor>
          ) : null}

          {block.kind === 'multiple_choice' ? (
            <div className={styles.editor}>
              <div>
                <label className="label" htmlFor={`prompt-${block.key}`}>
                  {t('customCompetition.questionLabel')}
                </label>
                <input
                  id={`prompt-${block.key}`}
                  className="input"
                  value={block.prompt}
                  maxLength={300}
                  placeholder={t('customCompetition.questionPlaceholder')}
                  onChange={(e) => onChange({ ...block, prompt: e.target.value })}
                />
              </div>

              <OptionGroup title={t('customCompetition.groupScoring')} defaultOpen>
                <div>
                  <p className="label">{t('customCompetition.pointsSystemLabel')}</p>
                  <div className={styles.radioGroup}>
                    <label className={styles.radioOption}>
                      <input
                        type="radio"
                        name={`scoring-${block.key}`}
                        checked={block.scoringMode === 'per_answer'}
                        onChange={() =>
                          onChange({ ...block, scoringMode: 'per_answer' as ScoringMode })
                        }
                      />
                      {t('customCompetition.pointsSystemPerAnswer')}
                    </label>
                    <label className={styles.radioOption}>
                      <input
                        type="radio"
                        name={`scoring-${block.key}`}
                        checked={block.scoringMode === 'winner_loser'}
                        onChange={() =>
                          onChange({ ...block, scoringMode: 'winner_loser' as ScoringMode })
                        }
                      />
                      {t('customCompetition.pointsSystemWinnerLoser')}
                    </label>
                  </div>
                </div>

                {block.scoringMode === 'per_answer' ? (
                  <div>
                    <p className="label">{t('customCompetition.pointsRangeLabel')}</p>
                    <div className={styles.rangeRow}>
                      <input
                        className="input"
                        type="number"
                        step={1}
                        value={block.minPoints}
                        aria-label={t('customCompetition.minPoints')}
                        placeholder={t('customCompetition.minPointsPlaceholder')}
                        onChange={(e) => onChange({ ...block, minPoints: e.target.value })}
                      />
                      <span className={styles.rangeSeparator}>–</span>
                      <input
                        className="input"
                        type="number"
                        step={1}
                        value={block.maxPoints}
                        aria-label={t('customCompetition.maxPoints')}
                        placeholder={t('customCompetition.maxPointsPlaceholder')}
                        onChange={(e) => onChange({ ...block, maxPoints: e.target.value })}
                      />
                    </div>
                    <p
                      className="muted"
                      style={{ fontSize: '0.75rem', marginTop: 'var(--space-1)' }}
                    >
                      {t('customCompetition.pointsRangeHint')}
                    </p>
                  </div>
                ) : null}
              </OptionGroup>

              <OptionGroup title={t('customCompetition.groupAppearance')}>
                <div>
                  <p className="label">{t('customCompetition.displayFormatLabel')}</p>
                  <div className={styles.radioGroup}>
                    <label className={styles.radioOption}>
                      <input
                        type="radio"
                        name={`display-format-${block.key}`}
                        checked={block.displayFormat === 'list'}
                        onChange={() =>
                          onChange({ ...block, displayFormat: 'list' as QuestionDisplayFormat })
                        }
                      />
                      {t('customCompetition.displayFormatList')}
                    </label>
                    <label className={styles.radioOption}>
                      <input
                        type="radio"
                        name={`display-format-${block.key}`}
                        checked={block.displayFormat === 'card'}
                        onChange={() =>
                          onChange({ ...block, displayFormat: 'card' as QuestionDisplayFormat })
                        }
                      />
                      {t('customCompetition.displayFormatCard')}
                    </label>
                  </div>
                  <p className="muted" style={{ fontSize: '0.75rem', marginTop: 'var(--space-1)' }}>
                    {t('customCompetition.displayFormatHint')}
                  </p>
                </div>

                <div>
                  <p className="label">{t('customCompetition.resultsFormatLabel')}</p>
                  <div className={styles.radioGroup}>
                    <label className={styles.radioOption}>
                      <input
                        type="radio"
                        name={`results-format-${block.key}`}
                        checked={block.resultsFormat === 'list'}
                        onChange={() =>
                          onChange({ ...block, resultsFormat: 'list' as QuestionResultsFormat })
                        }
                      />
                      {t('customCompetition.resultsFormatList')}
                    </label>
                    <label className={styles.radioOption}>
                      <input
                        type="radio"
                        name={`results-format-${block.key}`}
                        checked={block.resultsFormat === 'panels'}
                        onChange={() =>
                          onChange({ ...block, resultsFormat: 'panels' as QuestionResultsFormat })
                        }
                      />
                      {t('customCompetition.resultsFormatPanels')}
                    </label>
                  </div>
                  <p className="muted" style={{ fontSize: '0.75rem', marginTop: 'var(--space-1)' }}>
                    {t('customCompetition.resultsFormatHint')}
                  </p>
                </div>
              </OptionGroup>

              <OptionGroup title={t('customCompetition.groupAnswers')} defaultOpen>
                <div className={styles.answersList}>
                  {block.answers.map((answer, index) => (
                    <div key={answer.key} className={styles.answerItem}>
                      <div className={styles.answerRow}>
                        <input
                          className="input"
                          value={answer.label}
                          maxLength={200}
                          placeholder={t('customCompetition.answerPlaceholder', {
                            number: index + 1,
                          })}
                          onChange={(e) => patchAnswer(answer.key, { label: e.target.value })}
                        />
                        {block.answers.length > MIN_ANSWERS ? (
                          <button
                            type="button"
                            className={styles.removeAnswer}
                            aria-label={t('common.remove')}
                            onClick={() =>
                              onChange({
                                ...block,
                                answers: block.answers.filter((a) => a.key !== answer.key),
                              })
                            }
                          >
                            ×
                          </button>
                        ) : null}
                      </div>

                      {/* Only shown for the card layout, which is the only one
                          that renders any of it — see `AnswerAppearanceEditor`. */}
                      {block.displayFormat === 'card' ? (
                        <AnswerAppearanceEditor
                          value={answer}
                          onChange={(patch) => patchAnswer(answer.key, patch)}
                        />
                      ) : null}
                    </div>
                  ))}
                </div>

                {block.answers.length < MAX_ANSWERS ? (
                  <button
                    type="button"
                    className="button button-secondary"
                    onClick={() =>
                      onChange({ ...block, answers: [...block.answers, newAnswerDraft()] })
                    }
                  >
                    {t('customCompetition.addAnswer')}
                  </button>
                ) : null}
              </OptionGroup>

              <OptionGroup title={t('customCompetition.groupTiming')}>{timingFields}</OptionGroup>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

/**
 * A one-line summary for a block's collapsed header — the prompt for
 * `multiple_choice`/`date`, the two team names for a `football` question
 * where they're known, and a placeholder for anything not filled in yet.
 *
 * Deliberately reads only the draft itself, never a network response: a
 * database-sourced football match's friendly team names live in the query
 * results inside `FootballQuestionEditor`, not in `block`, so a match picked
 * that way falls back to the kind's own label instead of trying to reach
 * into a sibling component's data for it.
 */
function blockSummary(block: QuestionBlockDraft, t: TFunction): string {
  if (block.kind === '') return t('customCompetition.questionTypePlaceholder');

  if (block.kind === 'football') {
    if (
      block.footballSource === 'custom' &&
      block.footballHomeTeamName &&
      block.footballAwayTeamName
    ) {
      return t('customCompetition.footballMatchLine', {
        home: block.footballHomeTeamName,
        away: block.footballAwayTeamName,
      });
    }
    return t('customCompetition.kindFootball');
  }

  return block.prompt.trim() || t('customCompetition.questionSummaryUntitled');
}

/**
 * The `date`-kind editor (PLAN.md Phase 10.16): the prompt, which month the
 * calendar opens on, and how closeness turns into points — the one genuinely
 * new decision this kind introduces.
 *
 * There are no answers to author at all, which makes this the shortest of the
 * three editors: the organizer declares the stakes and nothing else, since the
 * "answers" are every day on a calendar and the result is a real moment nobody
 * knows yet.
 *
 * `children` is the shared deadline/lock-forever group, rendered last so all
 * three kinds end the same way.
 */
function DateQuestionEditor({
  block,
  onChange,
  children,
}: {
  block: QuestionBlockDraft;
  onChange: (next: QuestionBlockDraft) => void;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  // One at a time: reading two explanations side by side isn't the point, and
  // keeping several open pushes the radios themselves off screen.
  const [openHelp, setOpenHelp] = useState<DateScoringMode | null>(null);
  const modeText = useDateScoringText();

  return (
    <div className={styles.editor}>
      <div>
        <label className="label" htmlFor={`prompt-${block.key}`}>
          {t('customCompetition.questionLabel')}
        </label>
        <input
          id={`prompt-${block.key}`}
          className="input"
          value={block.prompt}
          maxLength={300}
          placeholder={t('customCompetition.datePromptPlaceholder')}
          onChange={(e) => onChange({ ...block, prompt: e.target.value })}
        />
      </div>

      <OptionGroup title={t('customCompetition.groupScoring')} defaultOpen>
        <div>
          <p className="label">{t('customCompetition.dateScoringLabel')}</p>
          <div className={styles.radioGroup}>
            {DATE_SCORING_MODES.map((mode) => (
              <RadioWithHelp
                key={mode}
                name={`date-scoring-${block.key}`}
                checked={block.dateScoringMode === mode}
                label={modeText[mode].label}
                help={modeText[mode].help}
                helpOpen={openHelp === mode}
                onToggleHelp={() => setOpenHelp((current) => (current === mode ? null : mode))}
                onSelect={() => onChange({ ...block, dateScoringMode: mode })}
              />
            ))}
          </div>
        </div>

        {/* `placement` pays nothing at all, so a range would be a rule nobody
            applies — see the `dateScoringMode` enum's doc comment. */}
        {block.dateScoringMode !== 'placement' ? (
          <div>
            <p className="label">{t('customCompetition.pointsRangeLabel')}</p>
            <div className={styles.rangeRow}>
              <input
                className="input"
                type="number"
                step={1}
                value={block.minPoints}
                aria-label={t('customCompetition.minPoints')}
                placeholder={t('customCompetition.minPointsPlaceholder')}
                onChange={(e) => onChange({ ...block, minPoints: e.target.value })}
              />
              <span className={styles.rangeSeparator}>–</span>
              <input
                className="input"
                type="number"
                step={1}
                value={block.maxPoints}
                aria-label={t('customCompetition.maxPoints')}
                placeholder={t('customCompetition.maxPointsPlaceholder')}
                onChange={(e) => onChange({ ...block, maxPoints: e.target.value })}
              />
            </div>
            <p className="muted" style={{ fontSize: '0.75rem', marginTop: 'var(--space-1)' }}>
              {t('customCompetition.dateRangeHint')}
            </p>
          </div>
        ) : null}
      </OptionGroup>

      <OptionGroup title={t('customCompetition.groupAppearance')}>
        <div>
          <label className="label" htmlFor={`date-month-${block.key}`}>
            {t('customCompetition.dateTargetMonthLabel')}
          </label>
          <DateField
            id={`date-month-${block.key}`}
            type="month"
            value={block.dateTargetMonth}
            onChange={(dateTargetMonth) => onChange({ ...block, dateTargetMonth })}
          />
          <p className="muted" style={{ fontSize: '0.75rem', marginTop: 'var(--space-1)' }}>
            {t('customCompetition.dateTargetMonthHint')}
          </p>
        </div>

        <div>
          <p className="label">{t('customCompetition.resultsFormatLabel')}</p>
          <div className={styles.radioGroup}>
            <label className={styles.radioOption}>
              <input
                type="radio"
                name={`results-format-${block.key}`}
                checked={block.resultsFormat === 'leaderboard'}
                onChange={() =>
                  onChange({ ...block, resultsFormat: 'leaderboard' as QuestionResultsFormat })
                }
              />
              {t('customCompetition.resultsFormatLeaderboard')}
            </label>
            <label className={styles.radioOption}>
              <input
                type="radio"
                name={`results-format-${block.key}`}
                checked={block.resultsFormat !== 'leaderboard'}
                onChange={() =>
                  onChange({ ...block, resultsFormat: 'list' as QuestionResultsFormat })
                }
              />
              {t('customCompetition.resultsFormatList')}
            </label>
          </div>
          <p className="muted" style={{ fontSize: '0.75rem', marginTop: 'var(--space-1)' }}>
            {t('customCompetition.dateResultsFormatHint')}
          </p>
        </div>
      </OptionGroup>

      {children}
    </div>
  );
}

/**
 * The `football`-kind editor: a source toggle (browse our own database, or
 * type a fully custom match), then either a cascading league→team→match
 * picker or two free-text team names + a penalties checkbox — all under one
 * "Match" category, since none of it makes sense without the others.
 * `children` is the deadline/lock-forever fields shared with the other two
 * kinds, rendered last so all three end the same way.
 */
function FootballQuestionEditor({
  block,
  onChange,
  children,
}: {
  block: QuestionBlockDraft;
  onChange: (next: QuestionBlockDraft) => void;
  children: React.ReactNode;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'en';

  const { data: competitions } = useQuery({
    queryKey: ['competitions'],
    queryFn: api.listCompetitions,
  });
  const footballCompetitions =
    competitions?.filter((c) => c.scoreable && c.sportId === 'football') ?? [];

  const { data: teamsData } = useQuery({
    queryKey: ['football-teams', block.footballCompetitionId, locale],
    queryFn: () => api.listFootballTeams(block.footballCompetitionId, locale),
    enabled: block.footballSource === 'database' && block.footballCompetitionId !== '',
  });

  const { data: matchesData } = useQuery({
    queryKey: [
      'football-upcoming-matches',
      block.footballCompetitionId,
      block.footballTeamId,
      locale,
    ],
    queryFn: () =>
      api.listUpcomingMatchesForTeam(block.footballCompetitionId, block.footballTeamId, locale),
    enabled: block.footballSource === 'database' && block.footballTeamId !== '',
  });

  return (
    <div className={styles.editor}>
      <OptionGroup title={t('customCompetition.groupMatch')} defaultOpen>
        <div>
          <p className="label">{t('customCompetition.footballSourceLabel')}</p>
          <div className={styles.radioGroup}>
            <label className={styles.radioOption}>
              <input
                type="radio"
                name={`football-source-${block.key}`}
                checked={block.footballSource === 'database'}
                onChange={() => onChange({ ...block, footballSource: 'database' })}
              />
              {t('customCompetition.footballSourceDatabase')}
            </label>
            <label className={styles.radioOption}>
              <input
                type="radio"
                name={`football-source-${block.key}`}
                checked={block.footballSource === 'custom'}
                onChange={() => onChange({ ...block, footballSource: 'custom' })}
              />
              {t('customCompetition.footballSourceCustom')}
            </label>
          </div>
        </div>

        {block.footballSource === 'database' ? (
          <>
            <div>
              <label className="label" htmlFor={`football-competition-${block.key}`}>
                {t('customCompetition.footballLeagueLabel')}
              </label>
              <select
                id={`football-competition-${block.key}`}
                className="select"
                value={block.footballCompetitionId}
                onChange={(e) =>
                  onChange({
                    ...block,
                    footballCompetitionId: e.target.value,
                    footballTeamId: '',
                    footballMatchId: '',
                  })
                }
              >
                <option value="">{t('customCompetition.footballLeaguePlaceholder')}</option>
                {footballCompetitions.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.displayName}
                  </option>
                ))}
              </select>
            </div>

            {block.footballCompetitionId ? (
              <div>
                <label className="label" htmlFor={`football-team-${block.key}`}>
                  {t('customCompetition.footballTeamLabel')}
                </label>
                <select
                  id={`football-team-${block.key}`}
                  className="select"
                  value={block.footballTeamId}
                  onChange={(e) =>
                    onChange({ ...block, footballTeamId: e.target.value, footballMatchId: '' })
                  }
                >
                  <option value="">{t('customCompetition.footballTeamPlaceholder')}</option>
                  {(teamsData?.teams ?? []).map((team) => (
                    <option key={team.id} value={team.id}>
                      {team.name}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}

            {block.footballTeamId ? (
              <div>
                <label className="label" htmlFor={`football-match-${block.key}`}>
                  {t('customCompetition.footballMatchLabel')}
                </label>
                <select
                  id={`football-match-${block.key}`}
                  className="select"
                  value={block.footballMatchId}
                  onChange={(e) => {
                    const matchId = e.target.value;
                    const match = matchesData?.matches.find((m) => m.id === matchId);
                    // Pre-fill the deadline from the real match's own kickoff —
                    // "when the game is going to be" is already known, not
                    // something to make the organizer retype — but only if
                    // they haven't already typed a deadline of their own for
                    // this block, so switching the match selection never
                    // clobbers a deliberate choice.
                    const prefill = match?.kickoffAt && !block.deadlineDate && !block.deadlineTime;
                    const split = prefill ? splitDateTime(match.kickoffAt) : null;
                    onChange({
                      ...block,
                      footballMatchId: matchId,
                      ...(split ? { deadlineDate: split.date, deadlineTime: split.time } : {}),
                    });
                  }}
                >
                  <option value="">{t('customCompetition.footballMatchPlaceholder')}</option>
                  {(matchesData?.matches ?? []).map((match) => {
                    const line = t('customCompetition.footballMatchLine', {
                      home: match.homeTeamName,
                      away: match.awayTeamName,
                    });
                    const dated = match.kickoffAt
                      ? `${line} — ${new Date(match.kickoffAt).toLocaleDateString(locale)}`
                      : line;
                    return (
                      <option key={match.id} value={match.id}>
                        {dated}
                      </option>
                    );
                  })}
                </select>
                {matchesData && matchesData.matches.length === 0 ? (
                  <p className="muted" style={{ fontSize: '0.8rem', marginTop: 'var(--space-1)' }}>
                    {t('customCompetition.footballNoUpcomingMatches')}
                  </p>
                ) : null}
              </div>
            ) : null}
          </>
        ) : (
          <>
            <div>
              <label className="label" htmlFor={`football-home-${block.key}`}>
                {t('customCompetition.footballHomeTeamLabel')}
              </label>
              <input
                id={`football-home-${block.key}`}
                className="input"
                value={block.footballHomeTeamName}
                maxLength={100}
                placeholder={t('customCompetition.footballHomeTeamPlaceholder')}
                onChange={(e) => onChange({ ...block, footballHomeTeamName: e.target.value })}
              />
            </div>
            <div>
              <label className="label" htmlFor={`football-away-${block.key}`}>
                {t('customCompetition.footballAwayTeamLabel')}
              </label>
              <input
                id={`football-away-${block.key}`}
                className="input"
                value={block.footballAwayTeamName}
                maxLength={100}
                placeholder={t('customCompetition.footballAwayTeamPlaceholder')}
                onChange={(e) => onChange({ ...block, footballAwayTeamName: e.target.value })}
              />
            </div>
            <label className={styles.lockToggle}>
              <input
                type="checkbox"
                checked={block.footballCanGoToPenalties}
                onChange={(e) => onChange({ ...block, footballCanGoToPenalties: e.target.checked })}
              />
              {t('customCompetition.footballCanGoToPenaltiesLabel')}
            </label>
          </>
        )}
      </OptionGroup>

      <OptionGroup title={t('customCompetition.groupScoring')}>
        <FootballAdvancedSettings block={block} onChange={onChange} />
      </OptionGroup>

      {children}
    </div>
  );
}

/**
 * The seven `MatchParams` a real match's ruleset stage would assign it
 * (`@bolao/scoring`), overridable per question since a custom football
 * question has no ruleset/stage to inherit them from. Every field is
 * optional: left blank, the server falls back to a group-stage-like
 * default, so an organizer who never opens the "Scoring" category still
 * gets a sensibly-scored question. Lives inside that category's
 * `OptionGroup` (`FootballQuestionEditor`) rather than owning its own
 * collapse toggle, so it reads as one more settings category alongside
 * "Match" and "Deadline & locking" instead of a second, differently-styled
 * escape hatch.
 */
function FootballAdvancedSettings({
  block,
  onChange,
}: {
  block: QuestionBlockDraft;
  onChange: (next: QuestionBlockDraft) => void;
}) {
  const { t } = useTranslation();

  return (
    <div className={styles.advancedGrid}>
      <p className="muted" style={{ fontSize: '0.75rem', gridColumn: '1 / -1' }}>
        {t('customCompetition.advancedSettingsHint')}
      </p>

      <label className={styles.advancedField}>
        {t('customCompetition.matchOutcomeLabel')}
        <input
          className="input"
          type="number"
          min={0}
          step={1}
          placeholder="2"
          value={block.footballOutcome}
          onChange={(e) => onChange({ ...block, footballOutcome: e.target.value })}
        />
      </label>

      <label className={styles.advancedField}>
        {t('customCompetition.matchExactLabel')}
        <input
          className="input"
          type="number"
          min={0}
          step={1}
          placeholder="2"
          value={block.footballExact}
          onChange={(e) => onChange({ ...block, footballExact: e.target.value })}
        />
      </label>

      <label className={styles.advancedField}>
        {t('customCompetition.matchPotMultiplierLabel')}
        <input
          className="input"
          type="number"
          min={0}
          step={0.1}
          placeholder="1"
          value={block.footballPotMultiplier}
          onChange={(e) => onChange({ ...block, footballPotMultiplier: e.target.value })}
        />
      </label>

      <label className={styles.advancedField}>
        {t('customCompetition.matchDecayLabel')}
        <input
          className="input"
          type="number"
          min={0}
          step={0.1}
          placeholder="0.9"
          value={block.footballDecay}
          onChange={(e) => onChange({ ...block, footballDecay: e.target.value })}
        />
      </label>

      <label className={styles.advancedField}>
        {t('customCompetition.matchThresholdLabel')}
        <input
          className="input"
          type="number"
          min={0}
          step={1}
          placeholder="4"
          value={block.footballThreshold}
          onChange={(e) => onChange({ ...block, footballThreshold: e.target.value })}
        />
      </label>

      <label className={styles.advancedField}>
        {t('customCompetition.matchPenaltyExactLabel')}
        <input
          className="input"
          type="number"
          min={0}
          step={1}
          placeholder={block.footballCanGoToPenalties ? '3' : '0'}
          value={block.footballPenaltyExact}
          onChange={(e) => onChange({ ...block, footballPenaltyExact: e.target.value })}
        />
      </label>

      <label className={styles.advancedField}>
        {t('customCompetition.matchPenaltyWrongLabel')}
        <input
          className="input"
          type="number"
          min={0}
          step={1}
          placeholder={block.footballCanGoToPenalties ? '1' : '0'}
          value={block.footballPenaltyWrong}
          onChange={(e) => onChange({ ...block, footballPenaltyWrong: e.target.value })}
        />
      </label>
    </div>
  );
}
